import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Check } from 'lucide-react';
import { Analytics } from '@/lib/analytics';
import { useOfertaCompleto } from '@/contexts/OfertaContext';

import mockupPainelAsset from '@/assets/mockup-painel-iphone-v4.png.asset.json';
const mockupPainel = mockupPainelAsset.url;
import mockupProjetosAsset from '@/assets/mockup-projetos-devices.png.asset.json';
const mockupProjetos = mockupProjetosAsset.url;
import mockupCrmAsset from '@/assets/mockup-crm-devices.png.asset.json';
const mockupCrm = mockupCrmAsset.url;
import mockupVitrineAsset from '@/assets/mockup-vitrine-laptop.png.asset.json';
const mockupVitrine = mockupVitrineAsset.url;

export type ShowcaseScene = 'painel' | 'projetos' | 'crm' | 'vitrine';

const SCENES: Record<
  ShowcaseScene,
  { src: string; alt: string; eyebrow: string; title: string; bullets: string[] }
> = {
  painel: {
    src: mockupPainel,
    alt: 'Mockup do Painel do Artesã em iPhone e MacBook',
    eyebrow: 'Painel',
    title: 'Sua rotina artesanal organizada num só lugar',
    bullets: [
      'Lembretes do dia já priorizados ao abrir',
      'Receita do mês e horas trabalhadas em tempo real',
      'Funciona no celular, tablet e computador',
    ],
  },
  projetos: {
    src: mockupProjetos,
    alt: 'Mockup de Projetos do Artesã em iPhone e MacBook',
    eyebrow: 'Projetos & Cronômetro',
    title: 'Saiba quanto tempo cada peça realmente leva',
    bullets: [
      'Cronômetro por projeto, com sessões salvas automaticamente',
      'Filtros por status: em andamento, aguardando, concluídos',
      'Fotos, técnica, materiais e prazo num só lugar',
    ],
  },
  crm: {
    src: mockupCrm,
    alt: 'Mockup do CRM e Análises do Artesã em iPhone e MacBook',
    eyebrow: 'CRM & Análises',
    title: 'Encomendas sob controle e números que decidem por você',
    bullets: [
      'Fluxo completo: orçamento, encomenda, produção e envio',
      'Faturamento mensal, ticket médio e técnicas mais vendidas',
      'Histórico por cliente para fidelizar com facilidade',
    ],
  },
  vitrine: {
    src: mockupVitrine,
    alt: 'Mockup da Calculadora de Preço e Vitrine Pública do Artesã',
    eyebrow: 'Precificação & Vitrine',
    title: 'Preço justo e uma loja online pronta para vender',
    bullets: [
      'Calculadora didática: materiais + horas + margem = preço seguro',
      'Vitrine pública com seu link, sua marca e seus produtos',
      'Capte pedidos direto pelo WhatsApp, sem comissões',
    ],
  },
};

interface DeviceShowcaseProps {
  scene: ShowcaseScene;
  reverse?: boolean;
  cta?: { label: string; to: string; source: string };
  className?: string;
  background?: 'cream' | 'transparent';
}

const DeviceShowcase = ({
  scene,
  reverse = false,
  cta = { label: 'Começar grátis', to: '/cadastro', source: 'device_showcase' },
  className = '',
  background = 'cream',
}: DeviceShowcaseProps) => {
  const completo = useOfertaCompleto();
  const ctaLabel = completo && /grátis|Avançado|planos/i.test(cta.label) ? (cta.to === '/planos' ? 'Ver preço' : 'Testar grátis por 15 dias') : cta.label;
  const s = SCENES[scene];
  const bg =
    background === 'cream'
      ? 'bg-gradient-to-b from-blush/30 via-background to-secondary/30'
      : '';

  return (
    <section className={`py-14 md:py-20 ${bg} ${className}`}>
      <div className="container max-w-6xl mx-auto">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center ${
            reverse ? 'lg:[&>*:first-child]:order-2' : ''
          }`}
        >
          <div className="order-2 lg:order-none">
            <Badge variant="secondary" className="mb-3 font-body">
              {s.eyebrow}
            </Badge>
            <h2 className="font-display text-2xl md:text-4xl font-semibold leading-tight mb-4">
              {s.title}
            </h2>
            <ul className="space-y-3 mb-7">
              {s.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="rounded-full bg-primary/10 p-1 mt-0.5 shrink-0">
                    <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  </span>
                  <span className="text-sm md:text-base text-foreground/85 leading-relaxed">
                    {b}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              asChild
              size="lg"
              className="font-body gap-2 shadow-lg shadow-primary/20"
            >
              <Link
                to={cta.to}
                onClick={() => Analytics.clickStartFree(cta.source)}
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>

          <div className="order-1 lg:order-none">
            <div className="relative">
              <div
                className="absolute -inset-4 md:-inset-8 rounded-[2rem] bg-gradient-to-tr from-primary/15 via-blush/30 to-transparent blur-2xl"
                aria-hidden="true"
              />
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                decoding="async"
                className={`relative h-auto rounded-2xl drop-shadow-2xl mx-auto ${
                  scene === 'painel' ? 'w-full max-w-[360px] md:max-w-[420px]' : 'w-full'
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeviceShowcase;
